import React, { useEffect, useCallback } from 'react';
import {
  IconClose, IconChevronLeft, IconChevronRight,
  IconDownload, IconOpenInNew, IconAudio, IconFile,
} from './icons';
import './MediaViewer.scss';

const isImageType = (item) =>
  /^image\//.test(item?.fileType || '') ||
  /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/i.test(item?.name || '');

const isVideoType = (item) =>
  /^video\//.test(item?.fileType || '') ||
  /\.(mp4|webm|mov|m4v|ogv)$/i.test(item?.name || '');

const isAudioType = (item) =>
  /^audio\//.test(item?.fileType || '') ||
  /\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(item?.name || '');

const isPdfType = (item) =>
  /pdf/i.test(item?.fileType || '') || /\.pdf$/i.test(item?.name || '');

const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return ''; }
};

const Body = ({ item }) => {
  if (!item) return null;
  if (isImageType(item)) {
    return <img src={item.url} alt={item.name} className="media-viewer__img" />;
  }
  if (isVideoType(item)) {
    return (
      <video controls autoPlay playsInline className="media-viewer__video" src={item.url}>
        Your browser doesn’t support video playback.
      </video>
    );
  }
  if (isAudioType(item)) {
    return (
      <div className="media-viewer__audio">
        <IconAudio size={96} />
        <audio controls autoPlay src={item.url}>
          Your browser doesn’t support audio.
        </audio>
      </div>
    );
  }
  if (isPdfType(item)) {
    return <iframe className="media-viewer__pdf" src={item.url} title={item.name} />;
  }
  return (
    <div className="media-viewer__fallback">
      <IconFile size={72} />
      <p>This file type can’t be previewed in the browser.</p>
      <a className="media-viewer__open-btn" href={item.url} target="_blank" rel="noopener noreferrer">
        Open in new tab
        <IconOpenInNew size={16} />
      </a>
    </div>
  );
};

const MediaViewer = ({ items, currentIndex, isOpen, onClose, onNext, onPrev }) => {
  const item = items?.[currentIndex];

  const handleKey = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight' && items.length > 1) onNext?.();
      else if (e.key === 'ArrowLeft' && items.length > 1) onPrev?.();
    },
    [isOpen, items?.length, onClose, onNext, onPrev]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, handleKey]);

  if (!isOpen || !item) return null;

  return (
    <div className="media-viewer" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="media-viewer__inner" onMouseDown={(e) => e.stopPropagation()}>
        <header className="media-viewer__header">
          <div className="media-viewer__title">
            <div className="media-viewer__name" title={item.name}>{item.name}</div>
            <div className="media-viewer__meta">
              {formatBytes(item.size)}
              {item.uploadedAt && <span className="media-viewer__sep">·</span>}
              {formatDate(item.uploadedAt)}
              {items.length > 1 && (
                <>
                  <span className="media-viewer__sep">·</span>
                  {currentIndex + 1} / {items.length}
                </>
              )}
            </div>
          </div>
          <div className="media-viewer__actions">
            <a
              className="media-viewer__icon-btn"
              href={item.url}
              download={item.name}
              title="Download"
            >
              <IconDownload size={20} />
            </a>
            <a
              className="media-viewer__icon-btn"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in new tab"
            >
              <IconOpenInNew size={20} />
            </a>
            <button className="media-viewer__icon-btn" onClick={onClose} title="Close (Esc)">
              <IconClose size={20} />
            </button>
          </div>
        </header>

        <div className="media-viewer__stage">
          <Body item={item} />
        </div>

        {items.length > 1 && (
          <>
            <button className="media-viewer__nav media-viewer__nav--prev" onClick={onPrev} title="Previous (←)">
              <IconChevronLeft size={28} />
            </button>
            <button className="media-viewer__nav media-viewer__nav--next" onClick={onNext} title="Next (→)">
              <IconChevronRight size={28} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MediaViewer;
